import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';

type Kalem = {
  productId: number; code: string; name: string; unit: string | null;
  beklenen: number; sayilan: number | null; fark: number | null;
  acilistanBeriDegisti: boolean;
};
type Oturum = {
  id: string; baslatanAd: string; baslangic: string; kanal: 'OFFICIAL' | 'OFFREC';
  kalemler: Kalem[]; sayilanAdet: number;
};

/**
 * SAYIM OTURUMU (şartname 4.2).
 *
 * Telefonda depo gezerek kullanılacak: her satır tek bir sayı kutusu, giriş
 * anında kaydediliyor. Oturum sunucuda duruyor — telefon kilitlenirse ya da
 * ertesi gün devam edilirse sayım kaybolmuyor.
 *
 * Bitirirken NOT ZORUNLU: üç ay sonra "burada niye 12 kutu eksik çıkmış"
 * sorusunun cevabı olmalı.
 */
export default function Sayim({ kapat }: { kapat: () => void }) {
  const [oturum, setOturum] = useState<Oturum | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [kanal, setKanal] = useState<'OFFICIAL' | 'OFFREC'>('OFFICIAL');
  const [ara, setAra] = useState('');
  const [yalnizKritik, setYalnizKritik] = useState(false);
  const [not, setNot] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [sonuc, setSonuc] = useState<{ sayilanAdet: number; farkliAdet: number } | null>(null);

  const yukle = useCallback(() => {
    setYukleniyor(true);
    api<{ oturum: Oturum | null }>('/api/sayim')
      .then((r) => setOturum(r.oturum))
      .catch((e) => setHata((e as Error).message))
      .finally(() => setYukleniyor(false));
  }, []);
  useEffect(yukle, [yukle]);

  async function basla() {
    setHata(null);
    try {
      await api('/api/sayim/basla', {
        method: 'POST',
        body: JSON.stringify({ kanal, ara: ara || undefined, yalnizKritik }),
      });
      yukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Başlatılamadı');
    }
  }

  /** Her dokunuşta sunucuya yazılır — telefonda sekme kapanırsa kayıp olmasın. */
  async function kalemYaz(productId: number, deger: string) {
    const sayilan = deger === '' ? null : Number(deger);
    setOturum((o) => o && {
      ...o,
      kalemler: o.kalemler.map((k) => (k.productId === productId
        ? { ...k, sayilan, fark: sayilan == null ? null : Math.round((sayilan - k.beklenen) * 1000) / 1000 }
        : k)),
    });
    await api('/api/sayim/kalem', {
      method: 'POST', body: JSON.stringify({ productId, sayilan }),
    }).catch(() => setHata('Kaydedilemedi — bağlantıyı kontrol edin'));
  }

  async function bitir() {
    setHata(null);
    try {
      const r = await api<{ sayilanAdet: number; farkliAdet: number }>('/api/sayim/bitir', {
        method: 'POST', body: JSON.stringify({ note: not }),
      });
      setSonuc(r);
      setOturum(null);
      setNot('');
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Bitirilemedi');
    }
  }

  async function iptal() {
    await api('/api/sayim', { method: 'DELETE' }).catch(() => undefined);
    setBilgi('Sayım iptal edildi — hiçbir hareket yazılmadı.');
    yukle();
  }

  const farkli = oturum?.kalemler.filter((k) => k.fark != null && k.fark !== 0) ?? [];

  return (
    <div className="ccd-drawer-arka" onClick={kapat} role="presentation">
      <aside className="ccd-drawer" onClick={(e) => e.stopPropagation()}
             role="dialog" aria-modal="true" aria-label="Sayım oturumu">
        <div className="ccd-stack">
          <div className="ccd-row-between">
            <div>
              <div className="ccd-eyebrow">Stok</div>
              <h2 className="ccd-heading">Sayım oturumu</h2>
            </div>
            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>Kapat</button>
          </div>

          {bilgi && (
            <div className="ccd-banner"><span className="ccd-banner-icon">✓</span><span>{bilgi}</span></div>
          )}
          {hata && (
            <div className="ccd-banner ccd-banner-danger">
              <span className="ccd-banner-icon">!</span><span>{hata}</span>
            </div>
          )}

          {sonuc && (
            <div className="ccd-banner">
              <span className="ccd-banner-icon" aria-hidden="true">✓</span>
              <div>
                <div style={{ fontWeight: 600 }}>Sayım tamamlandı</div>
                <div className="ccd-meta">
                  {sonuc.sayilanAdet} kalem sayıldı · {sonuc.farkliAdet} kalemde fark bulundu ve
                  SAYIM hareketi olarak işlendi.
                </div>
              </div>
            </div>
          )}

          {yukleniyor ? (
            <div className="ccd-inline-note">Yükleniyor…</div>
          ) : !oturum ? (
            <div className="ccd-card ccd-stack-sm">
              <div className="ccd-fieldset-title">Yeni sayım başlat</div>
              <div className="ccd-field">
                <label className="ccd-field-label" htmlFor="skanal">Hane</label>
                <select id="skanal" className="ccd-select" value={kanal}
                        onChange={(e) => setKanal(e.target.value as 'OFFICIAL')}>
                  <option value="OFFICIAL">Resmî</option>
                  <option value="OFFREC">Faturasız</option>
                </select>
              </div>
              <div className="ccd-field">
                <label className="ccd-field-label" htmlFor="sara">Süzgeç (isteğe bağlı)</label>
                <input id="sara" className="ccd-input" placeholder="ARTA… ya da Artisan"
                       value={ara} onChange={(e) => setAra(e.target.value)} />
              </div>
              <label className="ccd-row" style={{ gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={yalnizKritik}
                       onChange={(e) => setYalnizKritik(e.target.checked)} />
                <span className="ccd-meta">Yalnız eşik altındakiler (hızlı tur)</span>
              </label>
              <button className="ccd-btn ccd-btn-primary" onClick={basla}>Sayımı başlat</button>
            </div>
          ) : (
            <>
              <div className="ccd-banner">
                <span className="ccd-banner-icon" aria-hidden="true">#</span>
                <div className="ccd-meta">
                  <b>{oturum.id}</b> · {oturum.baslatanAd} ·{' '}
                  {oturum.kanal === 'OFFICIAL' ? 'resmî' : 'faturasız'} hane ·{' '}
                  {oturum.sayilanAdet}/{oturum.kalemler.length} sayıldı
                </div>
              </div>

              <div className="ccd-table-wrap">
                <table className="ccd-table">
                  <thead>
                    <tr>
                      <th className="ccd-th">Ürün</th>
                      <th className="ccd-th ccd-td-num">Kayıt</th>
                      <th className="ccd-th ccd-td-num">Sayılan</th>
                      <th className="ccd-th ccd-td-num">Fark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oturum.kalemler.map((k) => (
                      <tr key={k.productId} className="ccd-tr">
                        <td className="ccd-td">
                          <b className="ccd-mono">{k.code}</b>
                          <div className="ccd-meta">{k.name}</div>
                          {k.acilistanBeriDegisti && (
                            <div className="ccd-meta ccd-kpi-down">
                              sayım açıldığından beri stok değişti
                            </div>
                          )}
                        </td>
                        <td className="ccd-td ccd-td-num">{k.beklenen}</td>
                        <td className="ccd-td ccd-td-num">
                          <input className="ccd-input" type="number" step="any" inputMode="decimal"
                                 style={{ width: 90, textAlign: 'right' }}
                                 value={k.sayilan ?? ''}
                                 onChange={(e) => void kalemYaz(k.productId, e.target.value)} />
                        </td>
                        <td className="ccd-td ccd-td-num">
                          {k.fark == null ? <span className="ccd-muted">—</span>
                            : k.fark === 0 ? <span className="ccd-badge ccd-badge-success">0</span>
                            : <span className={k.fark > 0 ? 'ccd-kpi-up' : 'ccd-kpi-down'}>
                                {k.fark > 0 ? '+' : ''}{k.fark}
                              </span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="ccd-card ccd-stack-sm">
                <div className="ccd-fieldset-title">
                  Sayımı bitir {farkli.length > 0 && `· ${farkli.length} kalemde fark var`}
                </div>
                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor="snot">
                    Not<span className="ccd-required"> *</span>
                  </label>
                  <input id="snot" className="ccd-input" value={not}
                         onChange={(e) => setNot(e.target.value)}
                         placeholder="Yıl sonu sayımı · A rafı kırık kutular ayrıldı" />
                  <div className="ccd-field-hint">
                    Zorunlu — farkın sebebi yazılmazsa üç ay sonra kimse bilmiyor.
                  </div>
                </div>
                <div className="ccd-row" style={{ flexWrap: 'wrap' }}>
                  <button className="ccd-btn ccd-btn-primary" disabled={not.trim().length < 3}
                          onClick={bitir}>
                    Farkları işle ve bitir
                  </button>
                  <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={iptal}>
                    Sayımı iptal et
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
